import React, { useState, MouseEvent } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '../Icons/HelpOutlineIcon';
import formatCurrency from '../../utilities/formatCurrency';
import InvoiceDetails from '../../types/InvoiceDetails';
import TaxStrategyType from '../../types/TaxStrategyType';
import Order from '../../types/Order';
import Styles from './CostCalculationTable.module.scss';

type CostCalculationTableProps = {
  estimates: InvoiceDetails;
  order: Order;
  taxToBeCalculated?: boolean;
  taxStrategy: TaxStrategyType;
};

const CostCalculationTable = ({
  order,
  estimates,
  taxStrategy,
  taxToBeCalculated,
}: CostCalculationTableProps): JSX.Element => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = (event: MouseEvent): void => {
    event.preventDefault();
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  if (!order || !order.orderItems || order.orderItems.length < 1) {
    return <></>;
  }

  const itemsSubtotal = order.lineItemSubtotal || 0;

  return (
    <Grid
      item
      xs={12}
      paddingTop=".25rem"
      sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}` })}
    >
      <Table
        data-testid="calculation-table"
        sx={{
          '& .MuiTableRow-root': {
            border: 'none',
          },
          '& .MuiTableCell-root': {
            border: 'none',
            padding: '4px 16px',
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell>
              <Typography>Subtotal</Typography>
            </TableCell>
            <TableCell className="text-end">
              <Typography>{formatCurrency(itemsSubtotal)}</Typography>
            </TableCell>
          </TableRow>
          {taxStrategy !== 'None' && (
            <TableRow>
              <TableCell>
                <Typography>
                  {taxStrategy === 'TaxReimbursement' ? (
                    <a
                      href="#"
                      onClick={handleClickOpen}
                      className={Styles.ccrLink}
                    >
                      Consultant Cost Reimbursement <HelpOutlineIcon />
                    </a>
                  ) : (
                    'Sales Tax'
                  )}
                </Typography>
              </TableCell>
              {taxToBeCalculated ? (
                <TableCell className="text-end fst-italic">
                  <Typography>To be calculated in a future step</Typography>
                </TableCell>
              ) : (
                <TableCell className="text-end align-middle">
                  <Typography>
                    {formatCurrency(
                      order.salesTaxAmount || estimates.estimatedSalesTax || 0
                    )}
                  </Typography>
                </TableCell>
              )}
            </TableRow>
          )}
          {order.fulfillmentMethod === 'Shipping' && (
            <TableRow>
              <TableCell>
                <Typography>S&amp;H</Typography>
              </TableCell>
              <TableCell className="text-end">
                <Typography>
                  {formatCurrency(order.shippingAmount ?? 0)}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {((taxToBeCalculated && (order.shippingAmount ?? 0) > 0) ||
            ((order.shippingTaxAmount ?? 0) ||
              (estimates.estimatedShippingTax ?? 0)) > 0) &&
            taxStrategy === 'ChargeTax' && (
              <TableRow>
                <TableCell>
                  <Typography>S&amp;H Tax</Typography>
                </TableCell>
                {taxToBeCalculated ? (
                  <TableCell className="text-end fst-italic">
                    <Typography>To be calculated in a future step</Typography>
                  </TableCell>
                ) : (
                  <TableCell className="text-end">
                    <Typography>
                      {formatCurrency(
                        order.shippingTaxAmount ||
                          estimates.estimatedShippingTax ||
                          0
                      )}
                    </Typography>
                  </TableCell>
                )}
              </TableRow>
            )}
          <TableRow>
            <TableCell className="fw-bold">
              <Typography sx={{ alignContent: 'flex-end' }}>
                <strong>Order Total</strong>
              </Typography>
            </TableCell>
            <TableCell className="fw-bold">
              <Typography
                sx={{
                  fontSize: '1.625rem',
                  fontWeight: 400,
                }}
                className="text-end"
              >
                {formatCurrency(
                  taxToBeCalculated
                    ? order.shippingAmount ?? 0 + itemsSubtotal
                    : order.orderTotal || estimates.estimatedGrandTotal || 0
                )}
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-Consultant-Cost-Reimbursement"
        data-testid="ccr-info-dialog"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-Consultant-Cost-Reimbursement">
            &quot;Consultant Cost Reimbursement&quot; denotes the reimbursement
            for the retail sales tax previously paid by Consultants on these
            items.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={handleClose}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default CostCalculationTable;
